import type React from "react";
import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { Search, Building2, X, Loader2 } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";
import { Doc, Id } from "@/convex/_generated/dataModel";
import { useDebouncedValue } from "@/hooks/useDebounce";

type Company = Doc<"companies">;

interface CompanySearchSelectComboboxProps {
  onCompanySelect: (
    companyId: Id<"companies"> | null,
    company: Company | null,
  ) => void;
  initialCompanyId?: Id<"companies"> | null;
  excludeCompanyId?: Id<"companies"> | null;
  required?: boolean;
  placeholder?: string;
  disabled?: boolean;
  showClearButton?: boolean;
  limit?: number;
}

export default function CompanySearchSelectCombobox({
  onCompanySelect,
  initialCompanyId,
  excludeCompanyId,
  required = false,
  placeholder = "Buscar empresa...",
  disabled = false,
  showClearButton = false,
  limit = 10,
}: CompanySearchSelectComboboxProps) {
  const [open, setOpen] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { debouncedValue: search, isDebouncing } = useDebouncedValue(
    searchInput,
    500,
  );

  const companiesData = useQuery(api.companies.queries.getCompaniesForSelect, {
    page: 1,
    count: limit,
    search,
  });

  const selectedCompanyData = useQuery(
    api.companies.queries.getCompany,
    initialCompanyId ? { id: initialCompanyId } : "skip",
  );

  useEffect(() => {
    if (selectedCompanyData) {
      setSelectedCompany(selectedCompanyData);
    } else if (selectedCompanyData === null && initialCompanyId) {
      setSelectedCompany(null);
    } else if (!initialCompanyId) {
      setSelectedCompany(null);
    }
  }, [selectedCompanyData, initialCompanyId]);

  const companies = companiesData?.companies || [];

  const handleCompanySelect = (company: Company) => {
    if (excludeCompanyId && company._id === excludeCompanyId) return;
    setSelectedCompany(company);
    onCompanySelect(company._id, company);
    setOpen(false);
    setSearchInput("");
  };

  const handleClearSelection = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedCompany(null);
    onCompanySelect(null, null);
  };

  useEffect(() => {
    if (searchInput.length > 0) setOpen(true);
  }, [searchInput]);

  useEffect(() => {
    if (open) {
      if (selectedCompany?.name && !searchInput) {
        setSearchInput(selectedCompany.name);
      }
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  return (
    <div className="flex gap-1">
      <Popover
        open={open}
        onOpenChange={(v) => {
          if (!disabled) {
            setOpen(v);
            if (v && selectedCompany?.name && !searchInput)
              setSearchInput(selectedCompany.name);
            if (v) setTimeout(() => inputRef.current?.focus(), 0);
          }
        }}
      >
        <PopoverTrigger asChild>
          <div
            className="flex items-center w-full gap-1"
            onClick={(e) => e.preventDefault()}
          >
            <div className="flex-1 relative">
              <Search className="size-3 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <Input
                ref={inputRef}
                type="text"
                placeholder={
                  selectedCompany ? selectedCompany.name : placeholder
                }
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onClick={(e) => {
                  e.stopPropagation();
                  if (!disabled && !open) setOpen(true);
                }}
                onFocus={() => {
                  if (!disabled && !open) setOpen(true);
                }}
                disabled={disabled}
                className={`pl-7 ${required && !selectedCompany ? "border-primary ring-primary/30 ring-[0.2px]" : ""}`}
              />
            </div>
            {showClearButton && selectedCompany && !disabled && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      onClick={handleClearSelection}
                    >
                      <X className="text-muted-foreground" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Remover empresa</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </PopoverTrigger>

        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] p-0 flex flex-col overflow-hidden"
          align="start"
        >
          <div
            className="max-h-75 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
            onWheel={(e) => e.stopPropagation()}
          >
            {isDebouncing ? (
              <div className="flex items-center justify-center py-6 text-muted-foreground">
                <Loader2 className="animate-spin mr-2" />
                <span className="text-sm">Buscando...</span>
              </div>
            ) : companiesData === undefined ? (
              <div className="flex flex-col items-center justify-center py-6 text-muted-foreground px-4">
                <Building2 className="h-6 w-6 mb-2 text-muted-foreground/30" />
                <p className="text-sm">Cargando empresas...</p>
              </div>
            ) : companies.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-6 text-muted-foreground px-4">
                <Building2 className="h-6 w-6 mb-2 text-muted-foreground/30" />
                <p className="text-sm">No se encontraron empresas</p>
              </div>
            ) : (
              <div className="space-y-1 p-1">
                {companies.map((company: Company) => {
                  const isExcluded =
                    excludeCompanyId && company._id === excludeCompanyId;
                  const isSelected = selectedCompany?._id === company._id;
                  const isInactive =
                    company.status === "inactive" ||
                    company.status === "suspended";
                  const isDisabled = !!isExcluded;

                  return (
                    <button
                      key={company._id}
                      type="button"
                      onClick={() => handleCompanySelect(company)}
                      disabled={isDisabled}
                      className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md transition-colors text-left text-sm ${
                        isDisabled
                          ? "opacity-50 cursor-not-allowed bg-muted/40"
                          : isSelected
                            ? "ring-1 ring-primary bg-primary/5"
                            : "hover:bg-muted/70"
                      }`}
                    >
                      <Building2
                        className={`shrink-0 ${isDisabled ? "text-muted-foreground" : "text-primary"}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <p
                            className={`font-medium truncate ${isDisabled ? "text-muted-foreground" : "text-foreground"}`}
                          >
                            {company.name || "Empresa"}
                          </p>
                          {isInactive && (
                            <Badge
                              variant="outline"
                              className="text-[10px] px-1.5 py-0 h-4 border-border shrink-0"
                            >
                              Inactiva
                            </Badge>
                          )}
                          {isSelected && !isDisabled && (
                            <Badge
                              variant="secondary"
                              className="text-[10px] px-1.5 py-0 h-4 shrink-0"
                            >
                              Seleccionada
                            </Badge>
                          )}
                        </div>
                      </div>
                      {company.taxId && (
                        <Badge
                          variant="secondary"
                          className={`h-5 text-[10px] px-1.5 shrink-0 ${isDisabled ? "opacity-50" : ""}`}
                        >
                          {company.taxId}
                        </Badge>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
